import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { UI_VERSION } from 'src/app/components/support-info/support-info.component';
import { ApiService } from '../api/api.service';
import { Platform } from '@angular/cdk/platform';
import { interval, Subject } from 'rxjs';
import { ErrorInfo } from 'src/app/interfaces/error-handler.interface';
import { takeUntil } from 'rxjs/operators';

const LOGGING_INTERVAL = 10000;

@Injectable({
	providedIn: 'root',
})
export class GlobalErrorHandlerService implements OnDestroy {
	private recentErrors: Map<string, ErrorInfo> = new Map();

	private destroy: Subject<void> = new Subject();

	constructor(
		private apiService: ApiService,
		private router: Router,
		private platform: Platform
	) {
		// Log recent errors every LOGGING_INTERVAL
		interval(LOGGING_INTERVAL)
			.pipe(takeUntil(this.destroy))
			.subscribe(() => this.logRecentErrors());
	}

	ngOnDestroy() {
		this.destroy.next();
		this.destroy.complete();
	}

	public handleError(error: any) {
		console.error(error);

		// Only scripting errors should be logged
		if (!error?.stack) {
			return;
		}

		this.addError(error.name, error.message, error.stack);
	}

	public addError(name: string, message: string, stack: string = undefined) {
		if (!stack) {
			console.error({ name, message });
		}

		// If we already know about this error, just increment its count
		if (this.recentErrors.has(message)) {
			this.recentErrors.get(message).additionalInfo.count++;
			return;
		}

		// Determine client's browser information
		let browser: string;
		for (const p in this.platform) {
			if (p !== 'isBrowser' && this.platform[p]) {
				browser = p;
			}
		}

		// Create object representing the error
		const errorInfo: ErrorInfo = {
			error: name,
			additionalInfo: {
				error: message,
				page: this.router.url,
				count: 1,
				browser: `${browser} ${window.navigator.userAgent}`,
				version: UI_VERSION,
				stack,
			},
		};

		// Add this error to the recentErrors map
		this.recentErrors.set(message, errorInfo);
	}

	/** Attempt to send all recent errors to backend logging, and flush the map */
	public logRecentErrors() {
		Array.from(this.recentErrors.keys()).forEach(key => {
			const error: ErrorInfo = this.recentErrors.get(key);
			const prevCount = error.additionalInfo.count;
			this.apiService.postErrorLog(error).subscribe({
				next: () => {
					if (error.additionalInfo.count > prevCount) {
						error.additionalInfo.count -= prevCount;
					} else {
						this.recentErrors.delete(key);
					}
				},
			});
		});
	}
}

// Copyright (c) Motorola Solutions Inc. All Rights Reserved.
